import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { alpha, createTheme, Theme as MuiTheme } from '@mui/material/styles';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import type { CSSProperties } from 'react';
// Augments MUI theme Components type to include x-date-pickers component keys (e.g. MuiPickersInputBase)
import type {} from '@mui/x-charts/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

export const OPACITY_SELECTED = 0.08;
const OPACITY_FOCUS = 0.12;

/**
 * Custom theme properties
 * Extending the properties of mui
 */

interface ThemeCustomFont {
  primary: string;
  mono: string;
}

interface ThemeCustomBackground {
  level1: string;
  level2: string;
  level3: string;
  disabled: string;
  hover: string;
  overlay: string;
}

interface ThemeCustomStatuses {
  selected: string;
}

interface ThemeCustomAction {
  selected: string;
  active: string;
}

interface ThemeCustomStatus {
  primary: ThemeCustomStatuses;
  secondary: ThemeCustomStatuses;
  error: ThemeCustomStatuses;
  success: ThemeCustomStatuses;
  warning: ThemeCustomStatuses;
  focus: string;
}

interface ThemeCustomBorderAndDivider {
  default: string;
  hover: string;
  disabled: string;
  outlinedBorder: string;
}

interface ThemeCustomGradient {
  welcome: string;
}

interface ThemeCustom {
  background: ThemeCustomBackground;
  borderAndDivider: ThemeCustomBorderAndDivider;
  borderWidth: number;
  font: ThemeCustomFont;
  status: ThemeCustomStatus;
  action: ThemeCustomAction;
  gradient: ThemeCustomGradient;
  transparent: string;
}

/**
 * Extend vendor theme definitions
 */

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    icon: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsSizeOverrides {
    extraSmall: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    micro: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    micro: CSSProperties;
  }
  interface TypographyVariantsOptions {
    micro?: CSSProperties;
  }
}

declare module '@mui/material/SvgIcon/svgIconClasses' {
  interface SvgIconClasses {
    fontSizeExtraSmall: string;
  }
}

declare module '@mui/material/styles' {
  interface CustomTheme {
    custom: ThemeCustom;
  }
  interface Theme extends CustomTheme {}
  interface ThemeOptions extends CustomTheme {}
}

declare module '@emotion/react' {
  export interface Theme extends MuiTheme {}
}

/**
 * Mui theme definition
 * Figma naming convention for colors: 1: dark, 2: main, 3: light
 */
export const createMuiTheme = (mode: 'dark' | 'light'): MuiTheme => {
  const isLight = mode === 'light';
  const primaryMain = '#CCCC00';
  const secondaryMain = '#63666F';
  const errorMain = isLight ? '#D32F2F' : '#F44336';
  const successMain = isLight ? '#2E7D32' : '#66BB6A';
  const warningMain = '#ED6C02';
  const custom: ThemeCustom = {
    font: {
      primary: 'Rubik',
      mono: 'Courier New'
    },
    background: {
      level1: isLight ? '#F5F5F5' : '#161616',
      level2: isLight ? '#FAFAFA' : '#202020',
      level3: isLight ? '#FFFFFF' : '#252525',
      disabled: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
      hover: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
      overlay: alpha('#000000', 0.5)
    },
    borderAndDivider: {
      default: isLight ? '#E0E0E0' : '#444444',
      hover: isLight ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.38)',
      disabled: isLight ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)',
      outlinedBorder: 'rgba(204, 204, 0, 0.5)'
    },
    status: {
      primary: {
        selected: alpha(primaryMain, OPACITY_SELECTED)
      },
      secondary: {
        selected: alpha(secondaryMain, OPACITY_SELECTED)
      },
      error: {
        selected: alpha(errorMain, OPACITY_SELECTED)
      },
      success: {
        selected: alpha(successMain, OPACITY_SELECTED)
      },
      warning: {
        selected: alpha(warningMain, OPACITY_SELECTED)
      },
      focus: alpha(primaryMain, OPACITY_FOCUS)
    },
    action: {
      selected: isLight ? alpha('#000000', OPACITY_SELECTED) : alpha('#FFFFFF', OPACITY_SELECTED),
      active: isLight ? 'rgba(0, 0, 0, 0.56)' : 'rgba(255, 255, 255, 0.56)'
    },
    gradient: {
      welcome: 'linear-gradient(180deg, #515511 0%, #15150c 100%)'
    },
    borderWidth: 1,
    transparent: 'transparent'
  };

  return createTheme({
    palette: {
      mode,
      background: {
        default: custom.background.level2
      },
      text: {
        primary: isLight ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
        secondary: isLight ? 'rgba(0, 0, 0, 0.60)' : 'rgba(255, 255, 255, 0.60)',
        disabled: isLight ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.38)'
      },
      primary: {
        main: primaryMain,
        dark: '#A99F00',
        light: '#E0E341',
        contrastText: '#363636'
      },
      secondary: {
        main: secondaryMain,
        dark: '#37393F',
        light: '#898E9B',
        contrastText: '#FFFFFF'
      },
      error: {
        main: errorMain,
        dark: isLight ? '#CA2C2A' : '#CA2C2A',
        light: isLight ? '#5F2120' : '#F4C7C7',
        contrastText: '#FFFFFF'
      },
      warning: {
        main: warningMain,
        dark: isLight ? '#E46B00' : '#EC7B00',
        light: isLight ? '#663C00' : '#FFE2B7',
        contrastText: '#FFFFFF'
      },
      info: {
        main: isLight ? '#0288D1' : '#29B6F6',
        dark: isLight ? '#0288D1' : '#2F89D4',
        light: isLight ? '#014361' : '#B8E7FB',
        contrastText: '#FFFFFF'
      },
      success: {
        main: successMain,
        dark: isLight ? '#3A7D2D' : '#458E37',
        light: isLight ? '#1E4620' : '#CCE8CD',
        contrastText: '#FFFFFF'
      }
    },
    typography: {
      fontFamily: 'Rubik',
      h1: {
        fontSize: '96px',
        fontWeight: 300,
        lineHeight: '116.7%',
        letterSpacing: '-1.5px'
      },
      h2: {
        fontSize: '60px',
        fontWeight: 300,
        lineHeight: '120%',
        letterSpacing: '-0.5px'
      },
      h3: {
        fontSsize: '48px',
        fontWeight: 400,
        lineHeight: '116.7%'
      },
      h4: {
        fontSize: '34px',
        fontWeight: 400,
        lineHeight: '123.5%',
        letterSpacing: '0.25px'
      },
      h5: {
        fontSize: '24px',
        fontWeight: 400,
        lineHeight: '133.4%'
      },
      h6: {
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '160%,',
        letterSpacing: '0.15px'
      },
      subtitle1: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '175%',
        letterSpacing: '0.15px'
      },
      subtitle2: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '157%',
        letterSpacing: '0.1px'
      },
      body1: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '150%',
        letterSpacing: '0.15px'
      },
      body2: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '143%',
        letterSpacing: '0.17px'
      },
      caption: {
        fontSize: '12px',
        lineHeight: '166%',
        letterSpacing: '0.4px'
      },
      overline: {
        fontSize: '12px',
        lineHeight: '266%',
        letterSpacing: '1px',
        textTransform: 'uppercase'
      },
      micro: {
        fontSize: '10px',
        fontWeight: 500,
        lineHeight: '14px'
      },
      button: {
        fontWeight: 500
      },
      allVariants: {
        fontStyle: 'normal',
        fontWeight: 400
      }
    },
    shape: {
      borderRadius: 4
    },
    custom,
    components: {
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            fontWeight: '400',
            ...(ownerState.variant === 'filled' &&
              ownerState.severity && {
                color: theme.palette[ownerState.severity]?.contrastText
              })
          })
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          fontSizeExtraSmall: {
            fontSize: '16px'
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            micro: 'span'
          }
        }
      },
      MuiSnackbarContent: {
        styleOverrides: {
          action: {
            paddingTop: 0
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(ownerState.variant === 'icon' && {
              backgroundColor: theme.custom.background.level3,
              border: `solid 1px ${theme.custom.borderAndDivider.default}`,
              fontWeight: '400',
              textTransform: 'none'
            }),
            ...(ownerState.variant === 'text' &&
              ownerState.color === 'primary' && {
                color: theme.palette.text.primary
              }),
            ...(ownerState.variant === 'contained' &&
              ownerState.color === 'primary' && {
                color: theme.palette.primary.contrastText
              }),
            ...(ownerState.variant === 'contained' &&
              ownerState.color === 'error' && {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText
              })
          }),
          outlined: ({ ownerState, theme }) => ({
            ...((!ownerState.color || ownerState.color === 'primary' || ownerState.color === 'inherit') && {
              color: theme.palette.text.primary
            })
          })
        }
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(ownerState.color === 'secondary' && {
              backgroundColor: theme.custom.action.selected,
              color: theme.palette.text.secondary
            }),
            ...(ownerState.color === 'error' && {
              backgroundColor: theme.alpha(theme.palette.error.main, OPACITY_SELECTED),
              color: isLight ? theme.palette.error.light : theme.palette.error.contrastText
            }),
            ...(ownerState.color === 'warning' && {
              backgroundColor: theme.alpha(theme.palette.warning.main, OPACITY_SELECTED),
              color: isLight ? theme.palette.warning.light : theme.palette.warning.contrastText
            }),
            ...(ownerState.color === 'success' && {
              backgroundColor: theme.alpha(theme.palette.success.main, OPACITY_SELECTED),
              color: isLight ? theme.palette.success.light : theme.palette.success.contrastText
            }),
            ...(ownerState.color === 'info' && {
              backgroundColor: theme.alpha(theme.palette.info.main, OPACITY_SELECTED),
              color: isLight ? theme.palette.info.light : theme.palette.info.contrastText
            }),
            ...(ownerState.color === undefined && {
              backgroundColor: theme.custom.action.selected,
              color: theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.success.contrastText
            }),
            ...(ownerState.size === 'small' && {
              padding: '3px 4px'
            })
          })
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.custom.borderAndDivider.default
          })
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            background: theme.custom.background.level3,
            width: 'inherit'
          })
        }
      },
      MuiChartsGrid: {
        styleOverrides: {
          line: ({ theme }) => ({
            stroke: theme.custom.borderAndDivider.default,
            strokeDasharray: '3 3'
          })
        }
      },
      MuiChartsAxis: {
        styleOverrides: {
          // the axis line matches the grid lines, and the tick labels are dimmed. Both have to be set
          // here rather than per-chart: the charts' own `AxisRoot` styles these same class names with
          // equal specificity, so a `sx` on the chart loses on injection order, and `slotProps` sets
          // SVG attributes that any CSS rule overrides.
          root: ({ theme }) => ({
            [`& .${axisClasses.line}`]: {
              stroke: theme.custom.borderAndDivider.default
            },
            [`& .${axisClasses.tickLabel}`]: {
              fill: theme.palette.text.secondary
            }
          })
        }
      },
      MuiDataGrid: {
        defaultProps: {
          getRowSpacing: () => ({ top: 2, bottom: 2 })
        },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: 'transparent',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
              borderBottom: 'none'
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none', boxShadow: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
              boxShadow: 'none'
            },
            '& .MuiDataGrid-cell.Mui-focusVisible, & .MuiDataGrid-columnHeader.Mui-focusVisible': {
              outline: 'none',
              boxShadow: 'none'
            },
            '& .MuiDataGrid-columnHeader:hover': { backgroundColor: 'inherit' },
            '& .MuiDataGrid-columnHeader:focus .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader--siblingFocused .MuiDataGrid-columnSeparator':
              {
                opacity: 1
              },
            '&.background-level2 .MuiDataGrid-row': {
              backgroundColor: theme.custom.background.level2
            },
            '&.not-clickable .MuiDataGrid-row': {
              cursor: 'default',
              '&:hover': {
                backgroundColor: theme.custom.background.level3
              }
            },
            '&.background-level2.not-clickable .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: theme.custom.background.level2
              }
            },
            '& .MuiDataGrid-row.row-highlighted': {
              backgroundColor: theme.custom.action.selected,
              '&:hover': {
                backgroundColor: theme.custom.action.selected
              }
            }
          }),
          footerContainer: {
            borderTop: 'none'
          },
          columnHeaders: {
            backgroundColor: 'transparent',
            borderBottom: 'none'
          },
          columnHeader: {
            backgroundColor: 'transparent',
            borderBottom: 'none !important'
          },
          row: ({ theme }) => ({
            backgroundColor: theme.custom.background.level3,
            borderRadius: theme.shape.borderRadius,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.custom.background.hover
            },
            '&.Mui-selected:hover': {
              backgroundColor: theme.palette.action.selected
            }
          }),
          rowSkeleton: {
            marginTop: '2px',
            marginBottom: '2px'
          },
          cell: {
            border: 'none',
            backgroundClip: 'content-box',
            backgroundColor: 'none'
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.custom.borderAndDivider.default
          })
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.custom.borderAndDivider.default
          })
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: ({ theme }) => ({
            backgroundColor: theme.custom.background.level3
          })
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: () => ({
            border: 'none',
            margin: '6px 16px',
            height: '20px'
          })
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.custom.background.level3
          })
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            fontWeight: 'normal',
            lineHeight: 1.8,
            padding: '9px 15px',
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText
          })
        }
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            '& .clearButton': {
              opacity: '1 !important'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            ...(ownerState.variant === 'outlined' && {
              '& .MuiInputBase-root': {
                backgroundColor: 'transparent'
              },

              // notched outline
              [`& .${outlinedInputClasses.root}:not(.Mui-error):not(.Mui-focused) .${outlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: theme.custom.borderAndDivider.default
                },
              [`&:hover .${outlinedInputClasses.root}:not(.Mui-error):not(.Mui-disabled):not(.Mui-focused) .${outlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: theme.custom.borderAndDivider.hover
                },
              '&.info': {
                // notched border
                [`& .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: `${theme?.palette.info.main} !important`
                },
                // label
                [`& .${inputLabelClasses.outlined}`]: {
                  color: `${theme?.palette.info.main} !important`
                }
              },
              '&.warning': {
                // notched border
                [`& .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: `${theme?.palette.warning.main} !important`
                },
                // label
                [`& .${inputLabelClasses.outlined}`]: {
                  color: `${theme?.palette.warning.main} !important`
                }
              },
              '&.success': {
                // notched border
                [`& .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: `${theme.custom.borderAndDivider.default} !important`
                },
                // label
                [`& .${inputLabelClasses.outlined}`]: {
                  color: `${theme?.palette.text.secondary} !important`
                },
                //
                '& .MuiFormHelperText-root': {
                  color: `${theme?.palette.success.main} !important`
                }
              }
            })
          })
        }
      }
    }
  });
};
